import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/dashboard :: DashboardLayout
 *
 * Visual shell for the /dashboard page. Owns the page-level h1 + eyebrow
 * so the parent page stays focused on data wiring. Three content slots:
 *
 *   - `summary`: the CurrentProfileSummary card (right rail on desktop, top
 *     stack on mobile).
 *   - `switcher`: the StateSwitcher tab strip (above the main panel).
 *   - `main`: the RuleDiffPanel + any adjacencies (CDL prompt, etc.).
 *
 * Breakpoint behaviour:
 *   - Mobile/tablet (<sm): single column — summary → switcher → main.
 *   - Desktop (≥lg): two-column grid — main panel + switcher on the left,
 *     summary on the right. Matches the marketing "primary content with
 *     sidebar" layout from apps/marketing/HomePage.
 */
export interface DashboardLayoutProps {
  summary: ReactNode;
  switcher: ReactNode;
  main: ReactNode;
}

export default function DashboardLayout({
  summary,
  switcher,
  main,
}: DashboardLayoutProps) {
  const { t } = useTranslation();

  return (
    <section className="mx-auto max-w-6xl px-md pt-xl pb-xl sm:px-lg sm:pt-2xl sm:pb-2xl">
      <header className="max-w-3xl">
        <p className="text-sm font-semibold uppercase tracking-wide text-neutral-600">
          {t("dashboard.eyebrow")}
        </p>
        <h1 className="mt-sm font-display text-3xl font-bold tracking-tight text-brand-primary sm:text-4xl">
          {t("dashboard.title")}
        </h1>
        <div className="mt-md h-1 w-16 rounded-full bg-brand-accent" aria-hidden="true" />
        <p className="mt-md text-base text-neutral-800">{t("dashboard.subtitle")}</p>
      </header>

      <div className="mt-xl grid grid-cols-1 gap-lg lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div data-testid="dashboard-switcher-slot">{switcher}</div>
          <div className="mt-lg" data-testid="dashboard-main-slot">
            {main}
          </div>
        </div>
        <aside
          className="lg:col-span-1"
          data-testid="dashboard-summary-slot"
          aria-label={t("dashboard.profile.heading")}
        >
          {summary}
        </aside>
      </div>
    </section>
  );
}
